/** @jsxImportSource @emotion/react */
'use memo'
import css from '@emotion/css/macro'
import { WalletOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { formatCurrency } from '@sportninja/common/utils/utils'
import dayjs from 'dayjs'
import IndicatorCard from 'src/components/ts-components/IndicatorCard'
import { calculatePastDues } from '../../FinancialList'
import { useBillingContext } from '../context/BillingContext'

const BillingCards = () => {
  const { billingItems, currencyCode } = useBillingContext()

  const totalPaid = billingItems
    .filter((el) => el.paid)
    .reduce((acc, el) => acc + el.display_subtotal, 0)

  const totalOutstanding = billingItems
    .filter((el) => !el.paid || el.refunded)
    .reduce((acc, el) => {
      if (el.refunded && el.refund_subtotal < el.subtotal) {
        return acc + (el.subtotal - el.refund_subtotal)
      }
      return acc + el.subtotal
    }, 0)

  const totalAll = billingItems.reduce((acc, el) => acc + el.subtotal, 0)

  const nextPayment = billingItems.find((el) => !el.paid)

  const pastDue = calculatePastDues(billingItems)

  // SN-8425 — every figure here is an invoice subtotal, before tax; the team's
  // Team Budget tab shows cash including tax, so each card says which it is.
  const beforeTax = t('common:amountsBeforeTax', {})

  const iconNode = (
    <WalletOutlined
      css={css`
        font-size: 32px;
        color: ${colors.NEUTRAL_100};
        flex-shrink: 0;
      `}
    />
  )

  if (billingItems.length === 0) {
    return null
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: flex-start;
        gap: 16px;
        flex-shrink: 0;
        width: 100%;

        @media (max-width: 768px) {
          flex-direction: column;
          padding: 0 16px;
        }
      `}
    >
      <IndicatorCard
        icon={iconNode}
        title='Total Paid'
        value={`${formatCurrency(totalPaid, currencyCode)} / ${formatCurrency(
          totalAll,
          currencyCode
        )}`}
        subtitle={`Total Outstanding: ${formatCurrency(
          totalOutstanding,
          currencyCode
        )}`}
        hideSmallIndicator
        caption={beforeTax}
      />
      {nextPayment && (
        <IndicatorCard
          icon={iconNode}
          title='Next Payment'
          value={formatCurrency(nextPayment.subtotal, currencyCode)}
          subtitle={dayjs(nextPayment.due_date).format('MMMM DD, YYYY')}
          hideSmallIndicator
          caption={beforeTax}
        />
      )}
      <IndicatorCard
        icon={iconNode}
        title='Total Past Due'
        value={formatCurrency(pastDue, currencyCode)}
        subtitle=''
        hideSmallIndicator
        hideSubtitle
        caption={beforeTax}
      />
    </div>
  )
}

export default BillingCards
